import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ReportFilters = ({ onFiltersChange, activeFilters = {} }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [filters, setFilters] = useState(activeFilters);

  const handleFilterChange = (key, value) => {
    const updatedFilters = { ...filters, [key]: value };
    setFilters(updatedFilters);
    onFiltersChange && onFiltersChange(updatedFilters);
  };

  const clearFilters = () => {
    const emptyFilters = {};
    setFilters(emptyFilters);
    onFiltersChange && onFiltersChange(emptyFilters);
  };

  // Dynamic options - these would typically come from API
  const reportTypeOptions = [
    { value: '', label: 'All Types' },
    { value: 'business-overview', label: 'Business Overview' },
    { value: 'sales', label: 'Sales' },
    { value: 'receivables', label: 'Receivables' },
    { value: 'payables', label: 'Payables' },
    { value: 'tax-reports', label: 'Tax Reports' },
    { value: 'accountant', label: 'Accountant' }
  ];

  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'completed', label: 'Completed' },
    { value: 'pending', label: 'Pending' },
    { value: 'processing', label: 'Processing' },
    { value: 'failed', label: 'Failed' }
  ];

  const periodOptions = [
    { value: 'this-month', label: 'This Month' },
    { value: 'last-month', label: 'Last Month' },
    { value: 'this-quarter', label: 'This Quarter' },
    { value: 'last-quarter', label: 'Last Quarter' },
    { value: 'this-year', label: 'This Year' },
    { value: 'last-year', label: 'Last Year' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const createdByOptions = [
    { value: '', label: 'All Users' },
    { value: 'system', label: 'System Generated' }
    // TODO: Add dynamic user options from API
    // These would be populated from the backend user list
  ];

  const hasActiveFilters = Object.keys(filters)?.some(key => filters?.[key] && filters?.[key] !== '');

  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <Icon name="Filter" size={20} className="text-muted-foreground" />
          <h3 className="font-semibold text-foreground">Filters</h3>
          {hasActiveFilters && (
            <span className="px-2 py-1 text-xs bg-primary/10 text-primary rounded-full">
              {Object.keys(filters)?.filter(key => filters?.[key] && filters?.[key] !== '')?.length} active
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={clearFilters}
            disabled={!hasActiveFilters}
          >
            Clear All
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            iconName={isExpanded ? 'ChevronUp' : 'ChevronDown'}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? 'Less' : 'More'} Filters
          </Button>
        </div>
      </div>
      {/* Basic Filters - Always Visible */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Search Reports</label>
          <Input
            placeholder="Search by name or description..."
            value={filters?.search || ''}
            onChange={(e) => handleFilterChange('search', e?.target?.value)}
            className="w-full"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Report Type</label>
          <Select
            value={filters?.type || ''}
            onValueChange={(value) => handleFilterChange('type', value)}
            options={reportTypeOptions}
            placeholder="Select type..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Status</label>
          <Select
            value={filters?.status || ''}
            onValueChange={(value) => handleFilterChange('status', value)}
            options={statusOptions}
            placeholder="Select status..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Period</label>
          <Select
            value={filters?.period || 'this-month'}
            onValueChange={(value) => handleFilterChange('period', value)}
            options={periodOptions}
            placeholder="Select period..."
          />
        </div>
      </div>
      {/* Advanced Filters - Collapsible */}
      {isExpanded && (
        <div className="border-t border-border pt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Created By</label>
              <Select
                value={filters?.createdBy || ''}
                onValueChange={(value) => handleFilterChange('createdBy', value)}
                options={createdByOptions}
                placeholder="Select user..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Date From</label>
              <Input
                type="date"
                value={filters?.dateFrom || ''}
                onChange={(e) => handleFilterChange('dateFrom', e?.target?.value)}
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Date To</label>
              <Input
                type="date"
                value={filters?.dateTo || ''}
                onChange={(e) => handleFilterChange('dateTo', e?.target?.value)}
                className="w-full"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="hasAiInsights"
                checked={filters?.hasAiInsights || false}
                onChange={(e) => handleFilterChange('hasAiInsights', e?.target?.checked)}
                className="rounded border-border"
              />
              <label htmlFor="hasAiInsights" className="text-sm text-foreground">
                Only reports with AI insights
              </label>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="isScheduled"
                checked={filters?.isScheduled || false}
                onChange={(e) => handleFilterChange('isScheduled', e?.target?.checked)}
                className="rounded border-border"
              />
              <label htmlFor="isScheduled" className="text-sm text-foreground">
                Only scheduled reports
              </label>
            </div>
          </div>
        </div>
      )}
      {/* Active Filters Summary */}
      {hasActiveFilters && (
        <div className="border-t border-border pt-4 mt-4">
          <div className="flex items-center space-x-2 flex-wrap">
            <span className="text-sm font-medium text-muted-foreground">Active filters:</span>
            {Object.entries(filters)?.map(([key, value]) => {
              if (!value || value === '') return null;
              const displayValue = typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value;
              return (
                <span 
                  key={key} 
                  className="inline-flex items-center space-x-1 px-2 py-1 bg-primary/10 text-primary text-xs rounded-full"
                >
                  <span>{key}: {displayValue}</span>
                  <button 
                    onClick={() => handleFilterChange(key, '')}
                    className="hover:text-primary/70"
                  >
                    <Icon name="X" size={12} />
                  </button>
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportFilters;